"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { GOAL_OPTIONS, QUIZ_STEP_DEFINITIONS } from "./quizStepsConfig";
import {
  QUIZ_PROGRESS_STEPS,
  WIZARD_STEP_ORDER,
  type GoalResultVariant,
  type OnboardingWizardStep,
  type OnboardingWizardStepHandlers,
  type QuizAnswers,
} from "./types";
import { rootStore } from "@/src/store";
import {
  trackObQuestionAnswered,
  trackObQuestionView,
} from "@/src/hooks/onboarding/onboardingAnalytics";
import { persistWizardFurthestStep } from "@/src/hooks/onboarding/onboardingWizardSessionStorage";
import {
  getFirstIncompleteQuizStep,
  resolveGoalStepOrQuizRedirect,
} from "@/src/hooks/onboarding/quizAnswersReadiness";
import { getOnboardingV3Session } from "@/src/hooks/onboarding/onboardingV3SessionStorage";

const DEFAULT_ANSWERS = (): QuizAnswers => ({
  name: "",
  age: "",
  income: 120_000,
  expenses: 3_200,
  cash: 24_000,
  debt: 24_000,
  invest: 40_000,
  risk: "",
  goalId: "",
});

export interface UseOnboardingWizardStepOptions {
  initialStep?: OnboardingWizardStep;
  /** Test hook: force loading → loading-error */
  simulateLoadingError?: boolean;
  /** Harness: 覆盖 result 变体 */
  resultVariantOverride?: GoalResultVariant;
  /** Harness: 合并预填答案 */
  initialAnswers?: Partial<QuizAnswers>;
}

export const getNextWizardStep = (
  step: OnboardingWizardStep,
): OnboardingWizardStep | null => {
  const index = WIZARD_STEP_ORDER.indexOf(step);
  if (index < 0 || index >= WIZARD_STEP_ORDER.length - 1) {
    return null;
  }
  return WIZARD_STEP_ORDER[index + 1] ?? null;
};

export const getPrevWizardStep = (
  step: OnboardingWizardStep,
): OnboardingWizardStep | null => {
  if (step === "result") {
    const session = getOnboardingV3Session();
    return session?.fitnessScore ? "fitness-score-result" : "loading";
  }

  const index = WIZARD_STEP_ORDER.indexOf(step);
  if (index <= 0) {
    return null;
  }
  return WIZARD_STEP_ORDER[index - 1] ?? null;
};

export const resolveResultVariant = (goalId: string): GoalResultVariant => {
  const goal = GOAL_OPTIONS.find((g) => g.id === goalId);
  return goal?.resultVariant ?? "million";
};

export const computeNextWizardStep = (
  step: OnboardingWizardStep,
  answers: QuizAnswers,
): OnboardingWizardStep | null => {
  if (step === "loading" || step === "loading-error") {
    return null;
  }
  const next = getNextWizardStep(step);
  if (!next) {
    return null;
  }
  if (next === "goal") {
    const incomplete = getFirstIncompleteQuizStep(answers);
    if (incomplete) {
      return incomplete;
    }
  }
  return next;
};

export const useOnboardingWizardStep = (
  options: UseOnboardingWizardStepOptions = {},
): OnboardingWizardStepHandlers => {
  const { ownerInfo } = useSnapshot(rootStore.user);
  const prefilledName = ownerInfo?.nick_name?.trim() ?? "";

  const [step, setStep] = useState<OnboardingWizardStep>(
    options.initialStep ?? "score-intro",
  );
  const [answers, setAnswers] = useState<QuizAnswers>(() => ({
    ...DEFAULT_ANSWERS(),
    name: prefilledName,
    ...options.initialAnswers,
  }));

  useEffect(() => {
    persistWizardFurthestStep(step);
  }, [step]);

  useEffect(() => {
    trackObQuestionView(step);
  }, [step]);

  useEffect(() => {
    const redirect = resolveGoalStepOrQuizRedirect(step, answers);
    if (redirect !== step) {
      setStep(redirect);
    }
  }, [answers, step]);

  const displayName = answers.name.trim() || prefilledName || "there";

  const quizProgressIndex = useMemo(() => {
    const idx = QUIZ_PROGRESS_STEPS.indexOf(
      step as (typeof QUIZ_PROGRESS_STEPS)[number],
    );
    return idx >= 0 ? idx + 1 : 0;
  }, [step]);

  const resultVariant = useMemo(() => {
    if (options.resultVariantOverride) {
      return options.resultVariantOverride;
    }
    return resolveResultVariant(answers.goalId);
  }, [answers.goalId, options.resultVariantOverride]);

  const setAnswer = useCallback(
    <K extends keyof QuizAnswers>(key: K, value: QuizAnswers[K]) => {
      setAnswers((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const goNext = useCallback(
    (answersOverride?: QuizAnswers) => {
      trackObQuestionAnswered(step);
      const next = computeNextWizardStep(step, answersOverride ?? answers);
      if (next) {
        setStep(next);
      }
    },
    [answers, step],
  );

  const goBack = useCallback(() => {
    const prev = getPrevWizardStep(step);
    if (prev) {
      setStep(prev);
    }
  }, [step]);

  const retryLoading = useCallback(() => {
    setStep("loading");
  }, []);

  const completeLoading = useCallback(() => {
    const session = getOnboardingV3Session();
    setStep(session?.fitnessScore ? "fitness-score-result" : "result");
  }, []);

  const failLoading = useCallback(() => {
    setStep("loading-error");
  }, []);

  return {
    step,
    answers,
    quizProgressIndex,
    quizProgressTotal: QUIZ_PROGRESS_STEPS.length,
    resultVariant,
    displayName,
    goNext,
    goBack,
    setAnswer,
    retryLoading,
    completeLoading,
    failLoading,
    simulateLoadingError: Boolean(options.simulateLoadingError),
  };
};

export const getQuizDefinitionForStep = (step: OnboardingWizardStep) =>
  QUIZ_STEP_DEFINITIONS.find((def) => def.stepId === step);
